import React, { useEffect, useRef } from 'react';
import { Building2, BedDouble, Bath, Maximize, Zap, Info } from 'lucide-react';
import { usePropertyData } from '../../../../hooks/usePropertyData';
import FormField from './FormField';
import { useFormContext } from 'react-hook-form';
import { isLandPropertyType } from '../../../../utils/propertyApplicability';
import { validateBathroomCountInput } from '../../../../utils/propertyFormFeatures';

interface PropertyFeaturesSectionProps {
  className?: string;
}

const PropertyFeaturesSection: React.FC<PropertyFeaturesSectionProps> = ({ className = '' }) => {
  const { propertyStates, propertyTypes } = usePropertyData();
  const { watch, trigger } = useFormContext();
  const typeId = watch('type_id');
  const selectedType = propertyTypes.find(type => type.id === typeId) || { id: typeId };
  const land = isLandPropertyType(selectedType);
  const previousLand = useRef(land);

  useEffect(() => {
    if (previousLand.current !== land) {
      previousLand.current = land;
      // Aggiorna solo la validazione quando cambia l'applicabilità, non i dati.
      void trigger(['bathrooms', 'state_id']);
    }
  }, [land, trigger]);

  return (
    <div className={`bg-white rounded-lg shadow-sm p-6 mb-6 ${className}`}>
      <div className="flex items-center mb-4 pb-2 border-b">
        <Info className="h-5 w-5 text-primary mr-2" />
        <h4 className="font-medium text-gray-700">Caratteristiche</h4>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <FormField
          name="size"
          label="Superficie (m²)"
          type="number"
          icon={Maximize}
          required
          min={1}
          placeholder="Es. 100"
        />

        <FormField
          name="total_rooms"
          label="Locali Totali"
          type="number"
          icon={Building2}
          min={0}
          placeholder="Es. 4"
        />

        <FormField
          name="bedrooms"
          label="Camere da Letto"
          type="number"
          icon={BedDouble}
          min={0}
          placeholder="Es. 2"
        />

        <FormField
          name="bathrooms"
          label="Bagni"
          type="number"
          icon={Bath}
          required={!land}
          min={0}
          step={1}
          validate={value => validateBathroomCountInput(value, land)}
          placeholder={land ? 'Facoltativo' : 'Es. 1'}
          hint={land ? 'Facoltativo per i terreni. Indica i bagni solo se presenti; puoi lasciare vuoto o indicare 0.' : undefined}
        />

        <FormField
          name="state_id"
          label="Stato"
          icon={Building2}
          required={!land}
          placeholder={land ? 'Non indicato' : undefined}
          hint={land ? 'Facoltativo per i terreni. Lascia non indicato se non applicabile.' : undefined}
          options={propertyStates.map(state => ({
            value: state.id,
            label: state.name
          }))}
        />

        <FormField
          name="energy_class"
          label="Classe Energetica"
          icon={Zap}
          options={[
            { value: 'A4', label: 'A4' },
            { value: 'A3', label: 'A3' },
            { value: 'A2', label: 'A2' },
            { value: 'A1', label: 'A1' },
            { value: 'A', label: 'A' },
            { value: 'B', label: 'B' },
            { value: 'C', label: 'C' },
            { value: 'D', label: 'D' },
            { value: 'E', label: 'E' },
            { value: 'F', label: 'F' },
            { value: 'G', label: 'G' }
          ]}
        />
      </div>
    </div>
  );
};

export default PropertyFeaturesSection;